import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PENDING_ACTIONS_KEY } from '@/hooks/usePendingActions'
import { FACILITY_OVERVIEW_KEY } from '@/hooks/useFacility'
import { formatCalendarDateShort } from '@/lib/patient'
import { Loader2, AlertCircle, Lock, PlayCircle, UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { BackLink } from '@/components/navigation/BackLink'
import { PatientBanner } from '@/components/patient/PatientBanner'
import { Button } from '@/components/ui/button'
import { ProcedureFormSidebar } from '@/components/procedure/ProcedureFormSidebar'
import { ProcedureAttachmentsPanel, type ProcedureAttachmentEntry } from '@/components/procedure/ProcedureAttachmentsPanel'
import { useAttachmentsForSubmissions } from '@/hooks/useAttachments'
import { friendlyErrorMessage } from '@/lib/httpError'
import { ACTION_ITEMS_ACTION, APPLY_TEST_RESULTS_ACTION, offersCreateProduct, SKIP_ACTION } from '@/lib/formActions'
import { ActionItemDialog } from '@/components/action-items/ActionItemDialog'
import { useActionItemOpenCount } from '@/hooks/useActionItems'
import { formItemsLink, formSourcePath } from '@/lib/actionItems'
import { FormActionBar, SEND_TO_EPIC_ACTION } from '@/components/procedure/FormActionBar'
import { UnlockFormDialog } from '@/components/procedure/UnlockFormDialog'
import { SkipFormDialog } from '@/components/procedure/SkipFormDialog'
import { useAuditTrailInvalidator } from '@/hooks/useAuditTrail'
import { procedureJourneyQueryKey } from '@/hooks/useProcedureJourneys'
import { journeyCacheKey } from '@/lib/journeyCacheKey'
import {
  AnchoredFieldsPanel,
  type AnchoredFieldItem,
} from '@/components/procedure/AnchoredFieldsPanel'
import { FormRenderer } from '@/components/renderer/FormRenderer'
import { PopulateFromInstrument } from '@/components/integration-populate/PopulateFromInstrument'
import { LisReviewButton } from '@/components/lis-review/LisReviewButton'
import { LisReviewDeepLink } from '@/components/lis-review/LisReviewDeepLink'
import { LIS_SUBMISSION_PARAM } from '@/lib/lisReview'
import { ProcedureFlowPreviewDialog } from '@/components/procedures/ProcedureFlowPreviewDialog'
import { proceduresApi } from '@/api/proceduresApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { formsApi, type CompletionExtras } from '@/api/formsApi'
import { processStepsApi } from '@/api/processStepsApi'
import type { ParsedForm } from '@/lib/formExcel'
import { uploadedFormPayload } from '@/lib/uploadedForm'
import { anchorAllEligibleFields } from '@/lib/formAnchoring'
import { processFieldIds } from '@/lib/processFieldLibrary'
import { toSectionDefinitions } from '@/components/form-builder/FormDefinitionPanel'
import { pickListsApi } from '@/api/pickListsApi'
import { useTenant } from '@/providers/TenantProvider'
import { AddFormDialog } from '@/components/procedure/AddFormDialog'
import { CreateProductDialog } from '@/components/procedure/CreateProductDialog'
import { ReceiveProductDialog } from '@/components/procedure/ReceiveProductDialog'
import { SplitProductDialog } from '@/components/procedure/SplitProductDialog'
import { PoolProductDialog } from '@/components/procedure/PoolProductDialog'
import { SelectProductDialog } from '@/components/procedure/SelectProductDialog'
import { LabelPrintingDialog } from '@/components/procedure/LabelPrintingDialog'
import { LisOrderDialog } from '@/components/procedure/LisOrderDialog'
import { ApplyTestResultsDialog } from '@/components/procedure/ApplyTestResultsDialog'
import type { ApplyTestResultsResult } from '@/api/lisApi'
import { LisSendToEpicDialog } from '@/components/procedure/LisSendToEpicDialog'
import { ProductLabelPreviewDialog, storedSelectionsOf } from '@/components/procedure/ProductLabelPreviewDialog'
import { SplitLabelPreviewDialog } from '@/components/procedure/SplitLabelPreviewDialog'
import { ManageProductsDialog } from '@/components/procedure/ManageProductsDialog'
import { RecordedReconciliationBanner, RunLabelsDialog } from '@/components/labels/RunLabelsDialog'
import { LabelReconciliationDialog } from '@/components/labels/LabelReconciliationDialog'
import { useLabelReconciliation } from '@/hooks/useProductLabels'
import { labelCountsKey, needsLabelReview } from '@/lib/productLabels'
import { ShipProductDialog } from '@/components/shipping/ShipProductDialog'
import { UpdateProductVolumeDialog } from '@/components/procedure/UpdateProductVolumeDialog'
import { ProductAuditTrailDialog } from '@/components/procedure/ProductAuditTrailDialog'
import { LimsDetailsDialog } from '@/components/procedure/LimsDetailsDialog'
import { StorageAssignmentDialog } from '@/components/procedure/StorageAssignmentDialog'
import { BatchStorageAssignmentDialog } from '@/components/procedure/BatchStorageAssignmentDialog'
import { ProductNotCreatedDialog } from '@/components/procedure/ProductNotCreatedDialog'
import { PatientHistoryPanel } from '@/components/procedure/PatientHistoryPanel'
import { usePatientRuns } from '@/hooks/useProcedureBoard'
import {
  useCreatedProducts,
  useFormAssociatedProducts,
  usePatientProducts,
  useProcedureProducts,
} from '@/hooks/useProducts'
import { procedureMainProducts, productLabel } from '@/lib/procedureProductLabel'
import { workingProductState } from '@/lib/workingProducts'
import { useSetProductLocation } from '@/hooks/useStorage'
import type { CreatedProduct } from '@/types/products'
import type { ProductLabelSelections } from '@/lib/digitrax'
import { cellKey, resolveCell } from '@/lib/tableForm'
import { allFields } from '@/lib/formSections'
import {
  buildCrossFormSources,
  collectCrossFormRefs,
  resolveCrossRefs,
  type CrossFormResolution,
} from '@/lib/crossForm'
import { crossFormSubmissionId } from '@/lib/repeats'
import { patientNameFromSubmissions, referencedFormNames } from '@/lib/journey'
import { usePatientDetail } from '@/hooks/usePatientDetail'
import { patientName as patientRecordName } from '@/lib/patient'
import { useProcedureAssignableUsers } from '@/hooks/useProcedureAssignableUsers'
import { AssigneeSelect } from '@/components/procedure/AssigneeSelect'
import { assignedToLabel, deriveProcedureAssignedTo } from '@/lib/assignedTo'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { useInvalidateStepExecutions } from '@/hooks/useProcessSteps'
import { useAssignProcedure, useAssignStep } from '@/hooks/useProcedureAssignment'
import { useAuth } from '@/providers/AuthProvider'
import type {
  Procedure,
  ProcedureStatus,
  ProcedureJourney,
  ProcedureJourneyRepeat,
  ProcedureJourneyStep,
  FormDefinition,
  FormSubmission,
  FormCategory,
} from '@/types/cgt'
import { useCurrentCgtUser } from '@/hooks/useCurrentCgtUser'

// Colored status pill in the compact header line.
const PROCEDURE_STATUS_PILL: Record<ProcedureStatus, { label: string; className: string }> = {
  in_progress: { label: 'In progress', className: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', className: 'bg-slate-100 text-slate-600' },
  cancelled: { label: 'Cancelled', className: 'bg-rose-100 text-rose-700' },
}


/**
 * One patient procedure (`/patients/:mrn/procedures/:procedureId`).
 *
 * Left rail: the procedure's forms with live status. Main content: patient-level
 * anchored fields pinned across all forms, plus the selected form rendered inline.
 */
export function PatientProcedurePage({
  variant = 'patient',
}: { variant?: 'patient' | 'kit' | 'mes' | 'task' } = {}) {
  const params = useParams<{ mrn: string; procedureId: string }>()
  const procedureId = params.procedureId ?? ''
  // A KIT is the same procedure minus the patient (spec p2/p5): same template,
  // same steps, same forms, same process step. Rather than clone this page,
  // `kit` skips the patient lookup and swaps the header — everything else is
  // shared, which is the entire point.
  const isKit = variant === 'kit'
  // An MES order has no patient at all — not even a stand-in the way a kit run
  // carries its supply id in patientMrn (z8nrz7ckj9).
  const isMes = variant === 'mes'
  // A cleaning or maintenance task's procedure (z8nrz7ewt1): opened from the
  // Cleaning or Equipment page's Pending Actions, with no patient either.
  const isTask = variant === 'task'
  // A storage unit's task, opened from Inventory's Tasks tab (z8nrz7fjzc). Its
  // template is an Equipment one, so only the route tells it from equipment's.
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const onInventory = pathname.startsWith('/inventory/')
  // A CAPA has no patient either: it is raised against a RUN — a transplant, a
  // KIT ID, an MES work order — never against a person.
  /**
   * Nothing about a patient belongs on screen for these.
   *
   * A kit already hid its patient furniture; MES only ever blanked the `mrn`,
   * which left the header rendering the patient trail with an empty segment —
   * "Patients >  > CAPA template" — and an Active Patient badge on a record with
   * no patient. Grouped here so a fourth patient-less variant cannot inherit
   * that again.
   */
  const isPatientless = isKit || isMes || isTask
  const [procedure, setProcedure] = useState<Procedure | null>(null)

  /**
   * The subject reference every form/journey call is keyed on.
   *
   * The kit route has no `:mrn` segment, so it MUST come from the loaded
   * procedure — whose `patientMrn` carries the kit supply id (spec p3,
   * "Patient = LOT Number"). Reading the URL here left it empty, which asked
   * the API for the journey and submissions of nobody: the page rendered but
   * with no steps and no form, which looks exactly like "the kit has no form".
   */
  const mrn = isKit
    ? (procedure?.patientMrn ?? '')
    : isMes || isTask
      ? ''
      : (params.mrn ?? '')
  const { selectedTenantId } = useTenant()

  // "Order Test Results" mints a DIN for the selected step (the print/order moment).
  const [orderOpen, setOrderOpen] = useState(false)
  // Apply Test Results (z8nrz7g2wm): the dialog, the stage it last checked on
  // which form (its empty values stay flagged until filled), and what it applied.
  const [applyResultsOpen, setApplyResultsOpen] = useState(false)
  const [appliedResults, setAppliedResults] = useState<
    (ApplyTestResultsResult & { submissionId: string }) | null
  >(null)
  const [actionItemOpen, setActionItemOpen] = useState(false)
  // Send to Epic: open, and the product it opens on (null = the newest).
  const [epicOpen, setEpicOpen] = useState(false)
  const [epicProductId, setEpicProductId] = useState<string | null>(null)

  // "Add Form" / "Create CAPA" — ad-hoc form picker dialog.
  const [addFormOpen, setAddFormOpen] = useState(false)
  const [addFormCategory, setAddFormCategory] = useState<FormCategory>('exception')
  const [addFormLoading, setAddFormLoading] = useState(false)

  // Product Actions — Create: picker → DIN mint → Label Printing → storage
  // assignment; Receive: scan/generate DIN → storage assignment (spec: temp
  // placement for incoming bags). Plus the completion-gate popup for forms
  // configured with Create that reach Complete Form without a product.
  const [createProductOpen, setCreateProductOpen] = useState(false)
  const [receiveProductOpen, setReceiveProductOpen] = useState(false)
  const [splitProductOpen, setSplitProductOpen] = useState(false)
  const [poolProductOpen, setPoolProductOpen] = useState(false)
  // Update and Store both act on an EXISTING product, so each opens the same
  // picker first; `pickerIntent` says which dialog the choice feeds.
  const [pickerIntent, setPickerIntent] = useState<'volume' | 'store' | null>(null)
  // Split's own chain: split → label preview (print all / print each) →
  // ONE storage screen holding every aliquot. Placing them one dialog at a
  // time gave no sense of how many were left or what had already been chosen.
  const [splitLabelProducts, setSplitLabelProducts] = useState<CreatedProduct[]>([])
  const [batchStorageProducts, setBatchStorageProducts] = useState<CreatedProduct[]>([])
  const setProductLocation = useSetProductLocation()
  const [patientHistoryOpen, setPatientHistoryOpen] = useState(false)
  const [productGatePromptOpen, setProductGatePromptOpen] = useState(false)
  const [labelPrintProduct, setLabelPrintProduct] = useState<CreatedProduct | null>(null)
  const [labelPreviewProduct, setLabelPreviewProduct] = useState<CreatedProduct | null>(null)
  // What Label Printing was asked for, held only between print and preview.
  const [labelSelections, setLabelSelections] = useState<ProductLabelSelections | undefined>(
    undefined,
  )
  const [storageProduct, setStorageProduct] = useState<CreatedProduct | null>(null)
  const [manageProductsOpen, setManageProductsOpen] = useState(false)
  // Ship Product (z8nrz7eaex): the products the dialog opens with; null = closed.
  const [shipProducts, setShipProducts] = useState<CreatedProduct[] | null>(null)
  // The procedure's products: the list's default "All forms" tab, and the
  // procedure label's product names and P count — so fetched up front, not
  // only while the list is open.
  const procedureProductsQuery = useProcedureProducts(procedureId || null)
  const procedureProducts = procedureProductsQuery.data ?? []
  const mainProducts = useMemo(() => procedureMainProducts(procedureProducts), [procedureProducts])
  // "This patient" — fetched only while the list is open.
  const patientProductsQuery = usePatientProducts(
    isKit || isMes || isTask ? null : mrn || null,
    manageProductsOpen,
  )
  const [volumeProduct, setVolumeProduct] = useState<CreatedProduct | null>(null)
  const [auditProduct, setAuditProduct] = useState<CreatedProduct | null>(null)
  const [limsProduct, setLimsProduct] = useState<CreatedProduct | null>(null)
  /**
   * Which flow the Label Printing dialog is serving.
   *
   * `create` continues the spec's sequence into the designated-location
   * prompt; `relabel` is a reprint of a product that already exists (and is
   * very likely already placed), so it must stop at the printed label rather
   * than asking where to put a bag that has not moved.
   */
  const [labelFlow, setLabelFlow] = useState<'create' | 'relabel' | 'reprint'>('create')
  // This form's printed labels (z8nrz7dt48): the list, the Complete Form check
  // when they do not reconcile, and the reason a discrepancy was accepted with.
  const [labelsOpen, setLabelsOpen] = useState(false)
  const [labelReconcileOpen, setLabelReconcileOpen] = useState(false)
  const [labelDiscrepancy, setLabelDiscrepancy] = useState<{ submissionId: string; reason: string } | null>(null)
  // The label counts the completer was shown before signing (AC4), per form.
  const [labelReviewed, setLabelReviewed] = useState<{ submissionId: string; key: string } | null>(null)
  const [completeTrigger, setCompleteTrigger] = useState(0)
  // Submission ids the user explicitly chose "Skip" for — not state, since
  // adding to it must not itself cause a re-render. Keyed by submission (not a
  // one-shot flag) so a retried completion that fails an earlier validation
  // step can't leave a stale bypass for an unrelated later attempt.
  const skippedProductGateRef = useRef<Set<string>>(new Set())

  const [journey, setJourney] = useState<ProcedureJourney | null>(null)
  const [defsById, setDefsById] = useState<Map<string, FormDefinition>>(new Map())
  const [subsById, setSubsById] = useState<Map<string, FormSubmission>>(new Map())
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  /**
   * Which repeat of the selected step is open, when that step repeats.
   *
   * null means "the step itself" — a step that runs once, or a repeating step
   * whose group is collapsed. Each repeat is its own submission, so without
   * this the page cannot tell which of three Collections it is showing.
   */
  const [selectedRepeatIndex, setSelectedRepeatIndex] = useState<number | null>(null)
  const [searchParams] = useSearchParams()
  /**
   * The `?step=` / `?submission=` link last honoured, so each link is applied
   * once and then leaves the user alone, while a NEW link (the LIS review's
   * "Go to form" from this same page) still moves the selection.
   */
  const deepLinkApplied = useRef<string | null>(null)
  const [startingStepId, setStartingStepId] = useState<string | null>(null)
  const [flowPreviewOpen, setFlowPreviewOpen] = useState(false)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Bumped by the error banner's Retry. A failed initial load is often
  // transient — a rate limit clears within the minute (z8nrz7cnhz), a network
  // blip sooner — and the only recovery on offer before this was a full browser
  // reload, which costs every other request on the page a second time.
  const [reloadKey, setReloadKey] = useState(0)

  // Assign To (procedure-level cascade + per-form) — pickers only for permission holders.
  const { can } = usePermission()
  const { user } = useAuth()
  const canAssign = can(PERMISSIONS.proceduresAssign)
  // Unlocking a completed form is its own permission (not submissions.complete),
  // and the button is hidden, not disabled, without it.
  const canUnlock = can(PERMISSIONS.submissionsUnlock)
  // Skipping or unskipping a form of the run takes what completing it takes.
  const canSkip = can(PERMISSIONS.submissionsComplete)
  const [skipOpen, setSkipOpen] = useState(false)
  const canShip = can(PERMISSIONS.productsShip)
  const [unlockOpen, setUnlockOpen] = useState(false)
  const invalidateAuditTrail = useAuditTrailInvalidator()
  const queryClient = useQueryClient()
  // A cleaning / maintenance task's run (z8nrz7fgzw): completing its forms
  // completes the task server-side, so leaving the run refreshes the task lists
  // and the Overall View instead of showing them from cache.
  useEffect(() => {
    if (!isTask) return
    return () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-events'] })
      void queryClient.invalidateQueries({ queryKey: [PENDING_ACTIONS_KEY] })
      void queryClient.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] })
    }
  }, [isTask, queryClient])
  // Qualified for THIS run's procedure, not everyone who could be handed one
  // — the roster the picker offers is the capability matrix's answer for this
  // procedure family (Configure -> Method).
  const { users: assignableUsers } = useProcedureAssignableUsers(canAssign, procedureId)
  // Assignment changes who owns each process step, which the step cards read
  // from their own cached query — see the hook's note.
  const invalidateStepExecutions = useInvalidateStepExecutions()
  // Mutations, not direct api calls: that is what puts an assignment in front
  // of the cache-wide audit-trail refresh — see useProcedureAssignment.
  const assignProcedure = useAssignProcedure()
  const assignStep = useAssignStep()
  const [assignSaving, setAssignSaving] = useState(false)
  const [assignSavingStepId, setAssignSavingStepId] = useState<string | null>(null)
  /** "stepId:repeatIndex" of the repeat whose assignment is in flight. */
  const [assignSavingRepeatKey, setAssignSavingRepeatKey] = useState<string | null>(null)
  const [assignError, setAssignError] = useState<string | null>(null)

  // No Patient row exists for a kit run, so do not go looking for one.
  const patientQuery = usePatientDetail(isKit ? '' : mrn)

  const patientPath = `/patients/${encodeURIComponent(mrn)}`

  // The Patient record first — the name the banner shows. The form scan is a
  // fallback for a record with no name yet: it takes the first "first name" /
  // "last name" field in any form, which can be a donor's, and products used
  // to be stamped with that (the server now resolves the name itself too).
  const patientName = useMemo(
    () =>
      (patientQuery.data ? patientRecordName(patientQuery.data) : null) ??
      patientNameFromSubmissions(Array.from(subsById.values()), Array.from(defsById.values())),
    [patientQuery.data, subsById, defsById],
  )

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadJourney = useCallback(
    (procedureTemplateId: string, pId: string) =>
      procedureTemplatesApi.journey(procedureTemplateId, mrn, pId).then(setJourney),
    [mrn],
  )

  /**
   * The subject the initial load is keyed on, from the URL only.
   *
   * For a kit, `mrn` above is read from the loaded procedure, so keying the
   * load on it re-ran the whole load the moment the procedure arrived: every
   * request twice, and a second round trip in front of the form (z8nrz7e8kw).
   * The load already reads a kit's subject from the procedure it fetches.
   */
  const routeMrn = isKit ? '' : mrn

  // "Other procedures for this patient", with each one's progress: the H badge
  // and the Patient History panel. Beside the load, not in front of it, so the
  // form does not wait for it (z8nrz7e8kw). The procedure's OWN subject, as the
  // load uses: on the kit route `mrn` is empty. With no patient there are none,
  // and an empty subject would drop the MRN filter and return every procedure
  // in the tenant (z8nrz7ckj9).
  const historySubject = procedure ? ((isKit ? procedure.patientMrn : routeMrn) ?? '') : ''
  const otherProcedures = usePatientRuns(historySubject || null, procedure?.id ?? null)

  // Initial load: procedure → (journey, definitions, submissions) in parallel.
  useEffect(() => {
    if (!procedureId) return
    let cancelled = false
    setLoading(true)
    setError(null)

    proceduresApi
      .get(procedureId)
      .then(async p => {
        if (cancelled) return
        setProcedure(p)
        // Use the procedure's OWN subject ref: on the kit route `mrn` is still
        // empty on this first pass, and an empty key returns nothing.
        // A kit's subject ref is its supply id, which lives in patientMrn. That
        // column is nullable since z8nrz7ckj9, and every one of these calls is
        // keyed by subject — with none, they would query for "no patient" and
        // return other patient-less records rather than nothing.
        // For MES there is no subject at all: the procedure id is the entire
        // scope, and the server accepts that (z8nrz7ckj9). Passing an empty
        // subject with no procedure id is what rendered a page with no steps.
        const subjectRef = (isKit ? p.patientMrn : routeMrn) ?? ''
        const [j, subs] = await Promise.all([
          procedureTemplatesApi.journey(p.procedureTemplateId, subjectRef, p.id),
          // Name only: the definitions come from their own request below, and
          // nothing reads the copy each submission would otherwise embed.
          formsApi.getSubmissionsForPatient(subjectRef, p.id, 'name'),
        ])
        if (cancelled) return
        // Only the forms this case references, every version of each — never
        // the whole library, which is every version of every form in full
        // (z8nrz7e8kw). Every lookup below is by one of these, or by name.
        const defs = await formsApi.listDefinitionsByName(referencedFormNames(j.steps, subs))
        if (cancelled) return
        setJourney(j)
        setDefsById(new Map(defs.map(d => [d.id, d])))
        setSubsById(new Map(subs.map(s => [s.id, s])))
      })
      // friendlyErrorMessage, not `e.message`: a rate-limited load used to put
      // Nest's `ThrottlerException: Too Many Requests` on screen in place of the
      // whole case (z8nrz7cnhz). Every other message still shows verbatim —
      // those are server validation messages worth reading.
      .catch(e => !cancelled && setError(friendlyErrorMessage(e, 'Failed to load procedure.')))
      .finally(() => !cancelled && setLoading(false))

    return () => {
      cancelled = true
    }
  }, [procedureId, routeMrn, isKit, reloadKey])

  // Leaving this case marks its row on the procedures list stale, so the list
  // shows the progress made here the moment the user is back — whatever path
  // the writes took. Marked only: nothing refetches until the list mounts.
  const procedureTemplateId = procedure?.procedureTemplateId
  const procedurePatientMrn = procedure?.patientMrn ?? null
  const loadedProcedureId = procedure?.id
  useEffect(() => {
    if (!procedureTemplateId || !loadedProcedureId) return
    const key = procedureJourneyQueryKey(
      selectedTenantId,
      journeyCacheKey(procedurePatientMrn, procedureTemplateId, loadedProcedureId),
    )
    return () => {
      void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' })
    }
  }, [procedureTemplateId, procedurePatientMrn, loadedProcedureId, selectedTenantId, queryClient])

  // Default the selection to the first actionable step once the journey arrives.
  useEffect(() => {
    if (!journey || selectedStepId) return
    const next =
      journey.steps.find(s => s.status === 'in_progress' || s.status === 'not_started') ??
      journey.steps[0]
    if (next) setSelectedStepId(next.stepId)
  }, [journey, selectedStepId])

  /**
   * The pill reads the WORK, not the stored column.
   *
   * `Procedure.status` is only ever written on create and is never advanced to
   * `completed` by anything server-side, so a procedure whose every form is
   * finished still says "In progress" forever. The journey right here already
   * knows better — the sidebar renders "1 of 1 completed, 100%" off it — so the
   * pill derives from the same source rather than contradicting it a few pixels
   * away. `cancelled` is a real stored decision and always wins.
   *
   * The durable fix is for the backend to settle Procedure.status when the last
   * form completes; until it does, this keeps the header honest.
   */
  const headerStatus: ProcedureStatus = useMemo(() => {
    if (procedure?.status === 'cancelled') return 'cancelled'
    const steps = journey?.steps ?? []
    if (steps.length > 0 && steps.every(s => s.status === 'completed' || s.status === 'skipped')) return 'completed'
    return procedure?.status ?? 'in_progress'
  }, [procedure?.status, journey])

  // Ad-hoc submissions (added on the fly) injected as synthetic journey steps
  // after their originating step.
  const adHocStepIds = useMemo(() => new Set<string>(), [])
  const stepsWithAdHoc = useMemo<ProcedureJourneyStep[]>(() => {
    if (!journey) return []
    const adHocSubs = Array.from(subsById.values()).filter(s => s.isAdHoc)
    if (adHocSubs.length === 0) return journey.steps

    adHocStepIds.clear()
    const result: ProcedureJourneyStep[] = []
    for (const step of journey.steps) {
      result.push(step)
      const attached = adHocSubs.filter(s => s.addedFromStepId === step.stepId)
      for (const sub of attached) {
        const def = defsById.get(sub.formDefinitionId)
        const syntheticId = `adhoc:${sub.id}`
        adHocStepIds.add(syntheticId)
        // An uploaded form waits for the step it was added after (z8nrz7f030);
        // the server holds the same rule for its saves and completion.
        const waiting =
          sub.waitsForAddedFromStep &&
          step.status !== 'completed' &&
          step.status !== 'skipped' &&
          sub.status !== 'completed'
        result.push({
          stepId: syntheticId,
          sortOrder: step.sortOrder + 0.5,
          formDefinitionId: sub.formDefinitionId,
          formName: def?.name ?? 'Ad-hoc Form',
          status: waiting
            ? 'locked'
            : sub.status === 'completed'
              ? 'completed'
              : sub.status === 'skipped'
                ? 'skipped'
                : 'in_progress',
          submissionId: sub.id,
          blockedBy: waiting ? [{ stepId: step.stepId, formName: step.formName, reason: 'not_completed' }] : [],
        })
      }
    }
    const placed = new Set(adHocSubs.filter(s => s.addedFromStepId && journey.steps.some(st => st.stepId === s.addedFromStepId)).map(s => s.id))
    for (const sub of adHocSubs) {
      if (placed.has(sub.id)) continue
      const def = defsById.get(sub.formDefinitionId)
      const syntheticId = `adhoc:${sub.id}`
      adHocStepIds.add(syntheticId)
      // Uploaded from another ad-hoc form: waits for that one instead.
      const origin = sub.waitsForAddedFromStep && sub.addedFromStepId?.startsWith('adhoc:')
        ? subsById.get(sub.addedFromStepId.slice('adhoc:'.length))
        : undefined
      // A skipped origin is done for this too (z8nrz7fn5r), as the server reads it.
      const waiting =
        !!origin &&
        origin.status !== 'completed' &&
        origin.status !== 'skipped' &&
        sub.status !== 'completed' &&
        sub.status !== 'skipped'
      result.push({
        stepId: syntheticId,
        sortOrder: result.length,
        formDefinitionId: sub.formDefinitionId,
        formName: def?.name ?? 'Ad-hoc Form',
        status: waiting
          ? 'locked'
          : sub.status === 'completed'
            ? 'completed'
            : sub.status === 'skipped'
              ? 'skipped'
              : 'in_progress',
        submissionId: sub.id,
        blockedBy: waiting
          ? [{ stepId: sub.addedFromStepId!, formName: defsById.get(origin!.formDefinitionId)?.name ?? 'the form before it', reason: 'not_completed' }]
          : [],
      })
    }
    return result
  }, [journey, subsById, defsById, adHocStepIds])

  /**
   * `?step=` opens the page ON one form instead of the first actionable one.
   *
   * How a CAPA row is reached: a CAPA is an ad-hoc submission, which appears
   * here under the synthetic id `adhoc:<submissionId>`, and that step only
   * EXISTS once its submission has loaded — after the default selection above
   * has already run. So this applies late and exactly once: it waits for the
   * step to appear, then snaps to it, and never fights a click afterwards. A
   * link naming a step that never resolves simply leaves the default standing.
   *
   * `?submission=` names the form by its submission instead, which is all a
   * LIS result knows (z8nrz7fcu4); a repeat's submission selects that repeat.
   */
  useEffect(() => {
    const requestedStep = searchParams.get('step')
    const requestedSub = searchParams.get(LIS_SUBMISSION_PARAM)
    const key = requestedStep ? `step:${requestedStep}` : requestedSub ? `sub:${requestedSub}` : null
    if (!key || deepLinkApplied.current === key) return
    if (requestedStep) {
      if (!stepsWithAdHoc.some(s => s.stepId === requestedStep)) return
      deepLinkApplied.current = key
      setSelectedStepId(requestedStep)
      return
    }
    for (const s of stepsWithAdHoc) {
      const repeat = s.repeats?.find(r => r.submissionId === requestedSub)
      if (s.submissionId !== requestedSub && !repeat) continue
      deepLinkApplied.current = key
      setSelectedStepId(s.stepId)
      setSelectedRepeatIndex(repeat?.repeatIndex ?? null)
      return
    }
  }, [searchParams, stepsWithAdHoc])

  const selectedStep = stepsWithAdHoc.find(s => s.stepId === selectedStepId) ?? null
  const selectedDefinition = selectedStep
    ? defsById.get(selectedStep.formDefinitionId) ?? null
    : null
  /**
   * The repeat currently open, when one is. Re-read from the journey rather
   * than held in state so a refresh after completing a form updates its status
   * without the selection going stale.
   */
  const selectedRepeat =
    selectedRepeatIndex != null
      ? selectedStep?.repeats?.find(r => r.repeatIndex === selectedRepeatIndex) ?? null
      : null
  // A chosen repeat owns the submission; otherwise the step's own default one.
  const selectedSubmissionId = selectedRepeat
    ? selectedRepeat.submissionId
    : selectedStep?.submissionId ?? null
  const selectedSubmission = selectedSubmissionId
    ? subsById.get(selectedSubmissionId) ?? null
    : null
  const isAdHocStep = selectedStep ? adHocStepIds.has(selectedStep.stepId) : false

  /**
   * "+" → Skip Form: only on a form whose definition offers the "Skip Form"
   * action, open, of a run in progress, and one a person could start (its step
   * not locked). A form nobody opened yet is skipped through the same rules as
   * starting it — except a kit's, which starts through its own endpoint and is
   * not skipped before it is opened. Unskip is offered on any skipped form.
   */
  const selectedSlotStatus = selectedRepeat?.status ?? selectedStep?.status
  const skippable =
    canSkip &&
    !!selectedDefinition?.allowedActions?.includes(SKIP_ACTION) &&
    procedure?.status === 'in_progress' &&
    !!selectedStep &&
    selectedSlotStatus !== 'locked' &&
    (selectedSubmission
      ? selectedSubmission.status === 'active' || selectedSubmission.status === 'draft'
      : !isKit && !isAdHocStep)
  const unskippable = canSkip && procedure?.status === 'in_progress' && selectedSubmission?.status === 'skipped'

  // The product(s) this form is about (z8nrz7fxub): named beside "P", chosen
  // in its menu when several are in hand. A form not yet started, or closed,
  // cannot take a choice.
  const formEditable = selectedSubmission?.status === 'active' || selectedSubmission?.status === 'draft'
  const working = useMemo(
    () => workingProductState(mainProducts, selectedSubmission?.workingProductIds, formEditable),
    [mainProducts, selectedSubmission?.workingProductIds, formEditable],
  )

  // Action Items (z8nrz7fkfh): raised on the form open now, so it needs one
  // that has been started, and the key the routes behind it take.
  const offersActionItems =
    can(PERMISSIONS.actionItemsView) &&
    !!selectedSubmission &&
    !!selectedDefinition?.allowedActions?.includes(ACTION_ITEMS_ACTION)
  const actionItemCount = useActionItemOpenCount(selectedSubmission?.id ?? null, offersActionItems)

  // Cross-form references ({handle.fieldId}) on the selected form resolve
  // against the OTHER steps of this patient's journey — one shared mechanism
  // for formulas and visibility conditions (lib/crossForm). Rebuilt whenever
  // any sibling submission refreshes, so downstream values recompute live.
  const crossForm = useMemo<CrossFormResolution | undefined>(() => {
    if (!journey || !selectedDefinition || !selectedStep) return undefined
    const refs = collectCrossFormRefs(selectedDefinition)
    if (refs.length === 0) return undefined
    const sources = buildCrossFormSources(
      journey.steps
        .filter(s => s.formDefinitionId !== selectedStep.formDefinitionId)
        .map(s => {
          const def = defsById.get(s.formDefinitionId)
          // Repeat 1 for a repeating step, deliberately — NOT s.submissionId,
          // which moves to the next unfinished repeat and would make a
          // downstream formula change value as the case progressed.
          const sourceId = crossFormSubmissionId(s)
          const sub = sourceId ? subsById.get(sourceId) : undefined
          return {
            formHandle: def?.handle ?? s.formHandle,
            formName: s.formName,
            definition: def ?? null,
            values: sub ? sub.values : null,
            // Every repeat skipped: what reads it is ignored (z8nrz7fn5r).
            skipped: s.status === 'skipped',
          }
        }),
    )
    return resolveCrossRefs(refs, sources)
  }, [journey, selectedDefinition, selectedStep, defsById, subsById])

  // Products already created for the selected submission — drives both the
  // completion gate and (indirectly) whether "Create Product" has anything to
  // show already done.
  const { data: createdProducts = [] } = useCreatedProducts(selectedSubmission?.id ?? null)
  // What the Products list's "This form" tab and the "P" count show: the
  // products made here plus those this form split or pooled, with their aliquots
  // (z8nrz7fr1b). Wider than `createdProducts`, which stays the completion gate.
  const formProductsQuery = useFormAssociatedProducts(selectedSubmission?.id ?? null)
  const formProducts = formProductsQuery.data ?? []
  // No catalog-entry lookup here any more: the storage dialogs read each
  // product's own mapped storage condition (z8nrz7fpy8).
  const hasCreatedProduct = createdProducts.length > 0

  const labelSummary = useLabelReconciliation(selectedSubmission?.id ?? null)
  // Completing (or unlocking) writes the signed reconciliation the form then
  // shows (AC7), so the summary is re-read whenever the form's status moves.
  const refetchLabelSummary = labelSummary.refetch
  const selectedStatus = selectedSubmission?.status
  useEffect(() => {
    if (selectedStatus && selectedStatus !== 'active') void refetchLabelSummary()
  }, [selectedStatus, refetchLabelSummary])

  const handleBeforeComplete = useCallback(() => {
    const sid = selectedSubmission?.id
    /**
     * Asks only when this form OFFERS creating a product.
     *
     * It used to fire on the parent action alone, so a form configured with, say,
     * Store product but not Create new product would demand a product it has no
     * control to make. A form saved before operations could be chosen offers all
     * of them, so nothing already configured changes.
     */
    const productSkipped = !!sid && skippedProductGateRef.current.has(sid)
    if (!productSkipped && offersCreateProduct(selectedDefinition?.allowedActions) && !hasCreatedProduct) {
      setProductGatePromptOpen(true)
      return false
    }
    // A form that printed labels shows their reconciliation before it signs
    // (z8nrz7dt48 AC4): balanced, the counts and on; not balanced, review the
    // labels or go on with the discrepancy explained. The server checks the
    // same and refuses an unbalanced completion without a reason.
    const seen = labelReviewed && labelReviewed.submissionId === sid ? labelReviewed.key : null
    if (needsLabelReview(labelSummary.data, seen)) {
      setLabelReconcileOpen(true)
      return false
    }
    return true
  }, [selectedSubmission?.id, selectedDefinition, hasCreatedProduct, labelReviewed, labelSummary.data])

  const completionExtras = useMemo(
    () =>
      labelDiscrepancy && labelDiscrepancy.submissionId === selectedSubmission?.id
        ? { labelDiscrepancyReason: labelDiscrepancy.reason }
        : undefined,
    [labelDiscrepancy, selectedSubmission?.id],
  )

  function handleSkipProductGate() {
    setProductGatePromptOpen(false)
    // Keyed by submission (not a one-shot flag): the retried completion below
    // can itself fail an earlier check in FormRenderer (a required field, its
    // completionGate formula) before ever reaching this gate again, in which
    // case a one-shot "consume on next check" flag would go stale and silently
    // bypass the gate on a LATER, unrelated Complete Form click. Scoping the
    // exemption to this submission's id means it can only ever affect the form
    // the user actually chose to skip, however many attempts that takes.
    if (selectedSubmission?.id) skippedProductGateRef.current.add(selectedSubmission.id)
    setCompleteTrigger(t => t + 1)
  }

  const handleFormAction = async (label: string) => {
    if (label === ACTION_ITEMS_ACTION) {
      setActionItemOpen(true)
      return
    }
    if (label === 'Add Form') {
      setAddFormCategory('exception')
      setAddFormOpen(true)
      return
    }
    if (label === 'Create CAPA') {
      setAddFormCategory('capa')
      setAddFormOpen(true)
      return
    }
    if (label === APPLY_TEST_RESULTS_ACTION) {
      if (!selectedSubmission || !selectedDefinition) return
      setApplyResultsOpen(true)
      return
    }
    if (label === SEND_TO_EPIC_ACTION) {
      setEpicProductId(null)
      setEpicOpen(true)
      return
    }
    if (label !== 'Order Test Results') return
    if (!selectedTenantId || !selectedStep) return
    // Just open it. No DIN is minted here any more: a laboratory order is
    // placed against a PRODUCT, and that product already has its DIN. Minting
    // a fresh one named a bag that was not the one being tested.
    setOrderOpen(true)
  }

  // Attach a form to this run as an ad-hoc form, after the selected step —
  // Add Form's attachment, also used by Upload forms. Throws on failure.
  const attachAdHocForm = async (def: FormDefinition, opts: { waitsForAddedFromStep?: boolean } = {}) => {
    if (!procedure) throw new Error('This procedure is still loading.')
    const sub = await formsApi.createSubmission({
      formDefinitionId: def.id,
      ...(mrn ? { patientMrn: mrn } : {}),
      procedureId,
      isAdHoc: true,
      addedFromStepId: selectedStep?.stepId,
      ...(opts.waitsForAddedFromStep ? { waitsForAddedFromStep: true } : {}),
    })
    setSubsById(prev => new Map(prev).set(sub.id, sub))
    if (procedure.procedureTemplateId) {
      await loadJourney(procedure.procedureTemplateId, procedureId)
    }
    return sub
  }

  const handleAddFormSelect = async (def: FormDefinition) => {
    setAddFormLoading(true)
    try {
      await attachAdHocForm(def)
      setAddFormOpen(false)
    } catch (e) {
      console.error('Failed to add form:', e)
    } finally {
      setAddFormLoading(false)
    }
  }

  /**
   * Upload forms (z8nrz7f030): save the form read from the workbook — as the
   * form builder saves a new one — and attach it to this run as the next form
   * after the selected one. It stays locked until that form is completed, so
   * the sequence holds; opened straight away only when it already is.
   * Errors propagate so the dialog can say what failed.
   */
  const handleUploadForm = async (form: ParsedForm) => {
    const sections = form.category === 'anchored' ? anchorAllEligibleFields(form.sections) : form.sections
    const def = await formsApi.saveDefinition(uploadedFormPayload(form, toSectionDefinitions(sections)))
    // A Process field's configuration is anchored to the form on save, as the
    // builder does; best-effort there too.
    if (selectedTenantId) {
      await Promise.all(
        [...processFieldIds(sections)].map(fieldId =>
          processStepsApi.getOrCreate(selectedTenantId, fieldId, def.id).catch(() => undefined),
        ),
      )
    }
    setDefsById(prev => new Map(prev).set(def.id, def))
    const originDone = selectedStep?.status === 'completed'
    const sub = await attachAdHocForm(def, { waitsForAddedFromStep: !!selectedStep })
    if (originDone || !selectedStep) setSelectedStepId(`adhoc:${sub.id}`)
  }

  useEffect(() => {
    const sid = selectedStep?.submissionId
    if (!sid || subsById.has(sid)) return
    formsApi
      .getSubmission(sid)
      .then(sub => setSubsById(prev => new Map(prev).set(sub.id, sub)))
      .catch(console.error)
  }, [selectedStep?.submissionId, subsById])

  /**
   * The selected step's form, fetched by id when the page's batch did not
   * bring it — the same fallback the submission gets above.
   *
   * The batch (`listDefinitionsByName`) leaves out e2e test-data forms, so a
   * run built from them (an e2e kit on a shared tenant) had a step whose form
   * never arrived, and the form area spun forever. Fetched by id, the form
   * comes back whatever its flag; once per id, and a failure says so instead
   * of spinning.
   */
  const [missingDefinitionIds, setMissingDefinitionIds] = useState<Set<string>>(new Set())
  const definitionFetchAttempted = useRef(new Set<string>())
  const wantedDefinitionId = selectedStep?.formDefinitionId
  useEffect(() => {
    if (loading || !wantedDefinitionId || defsById.has(wantedDefinitionId)) return
    if (definitionFetchAttempted.current.has(wantedDefinitionId)) return
    definitionFetchAttempted.current.add(wantedDefinitionId)
    formsApi
      .getDefinition(wantedDefinitionId)
      .then(def => setDefsById(prev => new Map(prev).set(def.id, def)))
      .catch(e => {
        console.error(e)
        setMissingDefinitionIds(prev => new Set(prev).add(wantedDefinitionId))
      })
  }, [loading, wantedDefinitionId, defsById])

  // ── Derived: anchored fields across every form in the procedure ─────────────

  // Resolve a "latest" version *within the same lineage* as the step's
  // definition. We don't have a stable lineageId on FormDefinition yet, so we
  // approximate the lineage as: same name AND same category AND same creator,
  // AND a version >= the step's version. Without these guards a same-named but
  // unrelated form (admins can rename in place and don't enforce uniqueness)
  // could mis-anchor the panel. When no candidate fits, use the step's own def.
  const latestDefForStepDef = useCallback(
    (stepDef: FormDefinition): FormDefinition => {
      let best = stepDef
      for (const candidate of defsById.values()) {
        if (candidate.id === stepDef.id) continue
        if (candidate.name !== stepDef.name) continue
        if (candidate.category !== stepDef.category) continue
        if (candidate.createdBy !== stepDef.createdBy) continue
        if (candidate.version <= best.version) continue
        best = candidate
      }
      return best
    },
    [defsById],
  )

  const anchoredItems = useMemo<AnchoredFieldItem[]>(() => {
    if (!journey) return []
    const p = patientQuery.data
    const items: AnchoredFieldItem[] = []
    for (const step of journey.steps) {
      const stepDef = defsById.get(step.formDefinitionId)
      if (!stepDef) continue
      // Read anchored config from the resolved-latest version in the same
      // lineage; values come from this step's submission (field IDs are stable).
      const def = latestDefForStepDef(stepDef)
      const sub = step.submissionId ? subsById.get(step.submissionId) : undefined

      const formIsAnchored = def.category === 'anchored'
      for (const field of allFields(def)) {
        // Table fields: anchored cells live in field.tableConfig.cells
        if (field.fieldType === 'table' && field.tableConfig) {
          const tc = field.tableConfig
          for (const row of tc.rows) {
            for (const col of tc.columns) {
              const cell = tc.cells?.[cellKey(row.id, col.id)]
              if (!formIsAnchored && !cell?.anchored) continue
              const key = cellKey(row.id, col.id)
              const resolved = resolveCell(tc, row.id, col)
              // A cell marked not applicable never holds a value (z8nrz7eujb).
              if (resolved.disabled) continue
              items.push({
                key: `${step.stepId}:${key}`,
                fieldId: key,
                label: `${row.label} · ${col.label}`,
                fieldType: resolved.type === 'calculated' ? 'number' : resolved.type,
                formName: step.formName,
                value: sub?.values[key],
              })
            }
          }
          continue
        }
        if (!formIsAnchored && !field.anchored) continue
        let value: unknown = sub?.values[field.id]
        if (field.isMrField && field.mrFieldKey && p) {
          const raw = (p as unknown as Record<string, unknown>)[field.mrFieldKey]
          value = field.mrFieldKey === 'birthDate' && typeof raw === 'string'
            ? formatCalendarDateShort(raw) // calendar date, never shifted (z8nrz7dp77)
            : raw != null ? String(raw) : ''
        }
        items.push({
          key: `${step.stepId}:${field.id}`,
          fieldId: field.id,
          label: field.label,
          fieldType: field.fieldType,
          formName: step.formName,
          value,
        })
      }
    }
    return items
  }, [journey, defsById, subsById, latestDefForStepDef, patientQuery.data])

  const stepCategories = useMemo<Record<string, FormCategory>>(() => {
    if (!journey) return {}
    const map: Record<string, FormCategory> = {}
    for (const step of stepsWithAdHoc) {
      const def = defsById.get(step.formDefinitionId)
      if (def) map[step.stepId] = def.category
    }
    return map
  }, [journey, stepsWithAdHoc, defsById])

  // Evidence already filed on this procedure, for ProcedureAttachmentsPanel (an
  // always-visible strip above the open form — not the sidebar, not a hidden
  // icon). Only steps whose form actually declares an attachment are queried,
  // otherwise every procedure page would fire one request per step to learn nothing.
  // Restricted to COMPLETED steps: an in-progress step's attachment can still
  // be replaced or removed by whoever's filling it, so surfacing it as
  // "evidence" elsewhere would be presenting something provisional as settled.
  const attachmentStepIds = useMemo(() => {
    if (!journey) return [] as { stepId: string; submissionId: string; formName: string }[]
    return journey.steps.flatMap(step => {
      if (step.status !== 'completed' || !step.submissionId) return []
      const def = defsById.get(step.formDefinitionId)
      if (!def || !allFields(def).some(f => f.attachment?.enabled)) return []
      return [{ stepId: step.stepId, submissionId: step.submissionId, formName: step.formName }]
    })
  }, [journey, defsById])

  const attachmentsBySubmissionId = useAttachmentsForSubmissions(
    attachmentStepIds.map(s => s.submissionId),
  )

  // Flattened, and excludes the CURRENTLY open step — that step's own
  // attachments are already visible inline via each field's own icon, so
  // repeating them here would just show the same file in two places at once.
  const procedureAttachmentEntries = useMemo<ProcedureAttachmentEntry[]>(() => {
    const entries: ProcedureAttachmentEntry[] = []
    for (const { stepId, submissionId, formName } of attachmentStepIds) {
      if (stepId === selectedStepId) continue
      const found = attachmentsBySubmissionId.get(submissionId)
      if (!found) continue
      for (const attachment of found) entries.push({ submissionId, formName, attachment })
    }
    return entries
  }, [attachmentStepIds, attachmentsBySubmissionId, selectedStepId])

  const anchoredFieldStepIds = useMemo(() => {
    if (!journey) return new Set<string>()
    const ids = new Set<string>()
    for (const step of journey.steps) {
      const def = defsById.get(step.formDefinitionId)
      if (def && def.category !== 'anchored') {
        const hasAnchored = allFields(def).some(
          f => f.anchored ||
          (f.tableConfig?.cells != null && Object.values(f.tableConfig.cells).some(c => c.anchored)),
        )
        if (hasAnchored) ids.add(step.stepId)
      }
    }
    return ids
  }, [journey, defsById])

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleStart = useCallback(
    async (step: ProcedureJourneyStep, repeatIndex?: number | null) => {
      if (!procedure) return
      setStartingStepId(step.stepId)
      setError(null)
      try {
        if (isKit) {
          // A kit run starts through its own endpoint, not createSubmission:
          // the submission API validates `patientMrn` against /^MRN-.+$/ and a
          // kit's subject reference is its supply id (KIT-26-0031). Initiate
          // also creates the process-step execution the assembly work hangs
          // off, which a bare submission would not.
          // The step's own form: a run can hold several kit forms, each its own
          // kit (z8nrz7e11d), and without it the server only ever started the first.
          await pickListsApi.initiateKitRun(selectedTenantId as string, procedure.id, step.formDefinitionId)
        } else {
          const sub = await formsApi.createSubmission({
            formDefinitionId: step.formDefinitionId,
            ...(mrn ? { patientMrn: mrn } : {}),
            procedureTemplateId: procedure.procedureTemplateId,
            procedureId: procedure.id,
            // Only sent for a repeat the user actually picked. Omitted, the
            // server starts the lowest repeat not yet begun — and the server
            // REJECTS an index on a step that does not repeat, so this must
            // stay absent rather than default to 1.
            ...(repeatIndex != null ? { repeatIndex } : {}),
          })
          setSubsById(prev => new Map(prev).set(sub.id, sub))
        }
        await loadJourney(procedure.procedureTemplateId, procedure.id)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to start form.')
      } finally {
        setStartingStepId(null)
      }
    },
    [procedure, mrn, loadJourney, isKit, selectedTenantId],
  )

  // Procedure-level Assign To is derived from the journey's per-form assignments.
  const procedureAssignedTo = useMemo(
    () => (journey ? deriveProcedureAssignedTo(journey.steps) : undefined),
    [journey],
  )

  async function saveProcedureAssign(assigneeId: string | null) {
    if (!procedure || !procedureAssignedTo) return
    // No-op guard: picking the already-uniform assignee (or Unassigned on an
    // already-empty procedure) shouldn't fire the cascade.
    if (procedureAssignedTo.kind === 'user' && procedureAssignedTo.user.id === assigneeId) return
    if (procedureAssignedTo.kind === 'none' && assigneeId === null) return
    setAssignSaving(true)
    setAssignError(null)
    try {
      const { lockedSteps } = await assignProcedure.mutateAsync({
        id: procedure.id,
        assigneeId,
      })
      // Invalidate FIRST, before anything that can throw. The process-step cards
      // read a separate TanStack query with a five-minute staleTime, so without
      // this the assign lands, the sidebar updates, and every step card keeps
      // showing the previous owner until the operator reloads by hand. Ordered
      // after loadJourney it was also skipped whenever the journey refresh
      // failed — the assignment had persisted, yet the cards stayed stale and
      // the catch below reported an assignment failure.
      invalidateStepExecutions()
      await loadJourney(procedure.procedureTemplateId, procedure.id)
      // A step already under way keeps whoever signed its start. Saying so
      // beats a silent partial success on a control that reads as "assign all".
      if (lockedSteps) {
        setAssignError(
          `${lockedSteps} step${lockedSteps === 1 ? '' : 's'} already under way kept its assignee.`,
        )
      }
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Failed to assign the procedure.')
    } finally {
      setAssignSaving(false)
    }
  }

  async function saveStepAssign(step: ProcedureJourneyStep, assigneeId: string | null) {
    if (!procedure) return
    if ((step.assignee?.id ?? null) === assigneeId) return
    setAssignSavingStepId(step.stepId)
    setAssignError(null)
    try {
      await assignStep.mutateAsync({ id: procedure.id, stepId: step.stepId, assigneeId })
      // Same staleness — and same ordering reason — as the procedure-level
      // assign above: this changes one form's owner, its step cards are reading
      // the cached answer, and a failing journey refresh must not leave them
      // stale on a save that actually succeeded.
      invalidateStepExecutions()
      await loadJourney(procedure.procedureTemplateId, procedure.id)
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Failed to assign the form.')
    } finally {
      setAssignSavingStepId(null)
    }
  }

  /**
   * Assign ONE repeat of a repeating step.
   *
   * Compared against the repeat's CURRENT assignee, which may be inherited from
   * the step — so picking the person already shown does nothing, and picking
   * anyone else writes a repeat-specific row that overrides the step for this
   * performance alone.
   */
  async function saveRepeatAssign(
    step: ProcedureJourneyStep,
    repeat: ProcedureJourneyRepeat,
    assigneeId: string | null,
  ) {
    if (!procedure) return
    if ((repeat.assignee?.id ?? null) === assigneeId) return
    const key = `${step.stepId}:${repeat.repeatIndex}`
    setAssignSavingRepeatKey(key)
    setAssignError(null)
    try {
      await proceduresApi.assignStepRepeat(
        procedure.id,
        step.stepId,
        repeat.repeatIndex,
        assigneeId,
      )
      // Same ordering as the step-level assign: the step cards read a cached
      // answer, so refresh them before the journey rather than leaving them
      // stale on a save that succeeded.
      invalidateStepExecutions()
      await loadJourney(procedure.procedureTemplateId, procedure.id)
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : 'Failed to assign this repeat.')
    } finally {
      setAssignSavingRepeatKey(null)
    }
  }

  const handleSubmissionUpdated = useCallback(
    (updated: FormSubmission) => {
      setSubsById(prev => new Map(prev).set(updated.id, updated))
      // Refresh the journey so step statuses / progress reflect the change.
      if (procedure) loadJourney(procedure.procedureTemplateId, procedure.id).catch(console.error)
    },
    [procedure, loadJourney],
  )

  /**
   * Skip the open form and move on: to the step's next repeat still to do, else
   * the next form still to do in the run, else the first one anywhere.
   */
  const handleSkip = useCallback(async (reason: string) => {
    if (!procedure || !selectedStep) return
    setError(null)
    // A failed skip rejects, so the dialog stays open with the server's message.
    const skipped = selectedSubmission
      ? await formsApi.skipSubmission(selectedSubmission.id, reason)
      : await formsApi.skipNewSubmission({
          formDefinitionId: selectedStep.formDefinitionId,
          ...(mrn ? { patientMrn: mrn } : {}),
          procedureTemplateId: procedure.procedureTemplateId,
          procedureId: procedure.id,
          ...(selectedRepeatIndex != null ? { repeatIndex: selectedRepeatIndex } : {}),
          reason,
        })
    setSubsById(prev => new Map(prev).set(skipped.id, skipped))
    invalidateAuditTrail()
    // The skip is done from here on; failing to move on must not read as the
    // skip having failed, so the page just stays on the (now skipped) form.
    try {
      const fresh = await procedureTemplatesApi.journey(procedure.procedureTemplateId, mrn, procedure.id)
      setJourney(fresh)
      const open = (status: string) => status === 'in_progress' || status === 'not_started'
      const at = fresh.steps.findIndex(s => s.stepId === selectedStep.stepId)
      const sibling =
        selectedRepeatIndex != null
          ? fresh.steps[at]?.repeats?.find(r => r.repeatIndex !== selectedRepeatIndex && open(r.status))
          : undefined
      if (sibling) {
        setSelectedRepeatIndex(sibling.repeatIndex)
        return
      }
      const next = fresh.steps.slice(at + 1).find(s => open(s.status)) ?? fresh.steps.find(s => open(s.status))
      if (next) {
        setSelectedStepId(next.stepId)
        setSelectedRepeatIndex(null)
      }
    } catch (e) {
      console.error(e)
    }
  }, [procedure, selectedStep, selectedSubmission, selectedRepeatIndex, mrn, invalidateAuditTrail])

  /** Save which product(s) this form is about; the picker shows the error itself. */
  const handleSaveWorkingProducts = useCallback(
    async (productIds: string[]) => {
      if (!selectedSubmission) return
      handleSubmissionUpdated(await formsApi.setWorkingProducts(selectedSubmission.id, productIds))
      invalidateAuditTrail()
    },
    [selectedSubmission, handleSubmissionUpdated, invalidateAuditTrail],
  )

  /** Open a skipped form again, to be filled; it stays selected. */
  const handleUnskip = useCallback(async () => {
    if (!selectedSubmission) return
    setError(null)
    try {
      handleSubmissionUpdated(await formsApi.unskipSubmission(selectedSubmission.id))
      invalidateAuditTrail()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to unskip the form.')
    }
  }, [selectedSubmission, handleSubmissionUpdated, invalidateAuditTrail])

  // The renderer refreshes the audit trail after its own writes; an unlock
  // happens out here, so it has to do the same.
  const handleUnlocked = useCallback(
    (updated: FormSubmission) => {
      handleSubmissionUpdated(updated)
      invalidateAuditTrail()
      // An unlocked kit form's kit has left the shelf: refresh the kit lists.
      if (isKit) void queryClient.invalidateQueries({ queryKey: ['inventory', 'kits'] })
    },
    [handleSubmissionUpdated, invalidateAuditTrail, isKit, queryClient],
  )

  // An approved LIS result writes into this run's forms behind the page's back.
  const handleLisDecided = useCallback((outcome: 'approved' | 'rejected') => {
    if (outcome === 'approved') setReloadKey((k) => k + 1)
  }, [])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className='min-h-full bg-muted/20'>
      {/* A LIS review notification lands here with ?lisReview=<result id>. An
          approval writes into this run's forms, so reload them afterwards. */}
      <LisReviewDeepLink onDecided={handleLisDecided} currentSubmissionId={selectedSubmission?.id ?? null} />
      {/* The nav trail (crumbs · started date · status pill) rides inside the
          patient banner so the page has a single header row. The trail's last
          crumb doubles as the page title, and the crumb links replace the old
          Back button. */}
      {/* A kit run has no patient, so it gets a header carrying the same
          trail — crumbs, started, status, assignee — with the supply id where
          a person's name and MRN sit. Spec p3: for a kit, "Patient = LOT
          Number". */}
      {isKit && (
        <div className='border-b bg-card px-6 pb-3 pt-4'>
          <div className='flex min-w-0 items-baseline gap-3'>
            <h1 className='text-lg font-bold tracking-tight'>
              {procedure?.patientMrn ?? 'Kit run'}
            </h1>
            <span className='text-sm font-semibold text-blue-700'>
              {procedure?.procedureTemplate.name}
            </span>
          </div>
        </div>
      )}
      <PatientBanner
        mrn={isPatientless ? '' : mrn}
        displayName={isPatientless ? '' : patientName}
        hidePatient={isPatientless}
        trail={
          <div className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1'>
            {/* Additive, not a replacement: the trail below is a real hierarchy
                (Patients > MRN > Procedure), which one caller's origin should
                not overwrite. Inside `trail` so it stays in the sticky header
                rather than scrolling away (z8nrz7czj8). */}
            <BackLink />
            <Breadcrumbs
              items={
                isKit
                  ? [
                      { label: 'Kits', to: '/kits' },
                      { label: procedure?.procedureTemplate.name ?? 'Kit run' },
                    ]
                  : isMes
                    ? // Its own trail, not the patient one with a hole in it.
                      [
                        { label: 'MES', to: '/mes' },
                        { label: procedure?.mfgNumber ?? procedure?.procedureTemplate.name ?? 'MES order' },
                      ]
                    : isTask
                      ? // Back to the page whose Pending Actions opened it.
                        onInventory
                        ? [
                            { label: 'Inventory', to: '/inventory?tab=tasks' },
                            { label: procedure?.procedureTemplate.name ?? 'Storage procedure' },
                          ]
                        : procedure?.procedureTemplate.type === 'Equipment'
                        ? [
                            { label: 'Equipment', to: '/equipment?tab=pending-actions' },
                            { label: procedure.procedureTemplate.name },
                          ]
                        : [
                            { label: 'Cleaning', to: '/cleaning?tab=pending-actions' },
                            { label: procedure?.procedureTemplate.name ?? 'Cleaning procedure' },
                          ]
                    : [
                        { label: 'Patients', to: '/patients' },
                        { label: mrn, to: patientPath },
                        { label: procedure?.procedureTemplate.name ?? 'Procedure' },
                      ]
              }
            />
            {procedure && (
              <>
                <span className='text-xs text-muted-foreground'>
                  Started {new Date(procedure.startedAt).toLocaleDateString()}
                </span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium',
                    PROCEDURE_STATUS_PILL[headerStatus].className,
                  )}
                >
                  {PROCEDURE_STATUS_PILL[headerStatus].label}
                </span>
                <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                  <UserRound className='h-3.5 w-3.5' />
                  Assigned:
                  {canAssign ? (
                    <>
                      <AssigneeSelect
                        users={assignableUsers}
                        value={procedureAssignedTo?.kind === 'user' ? procedureAssignedTo.user : null}
                        placeholder={assignedToLabel(procedureAssignedTo)}
                        disabled={assignSaving}
                        meId={user?.userId}
                        onChange={saveProcedureAssign}
                        className='w-44'
                        buttonClassName='h-7 px-2 text-xs'
                      />
                      {/* Claiming your own run is the common case and the one
                          gate on Initiate, so it gets a control of its own
                          rather than a name to find in a list of everyone. */}
                      {user?.userId && procedureAssignedTo?.kind !== 'user' && (
                        <button
                          type='button'
                          disabled={assignSaving}
                          onClick={() => saveProcedureAssign(user.userId as string)}
                          className='rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-60'
                        >
                          Assign to me
                        </button>
                      )}
                    </>
                  ) : (
                    <span className='font-medium text-foreground'>
                      {assignedToLabel(procedureAssignedTo)}
                    </span>
                  )}
                  {assignError && <span className='text-destructive'>{assignError}</span>}
                </span>
              </>
            )}
          </div>
        }
      />
      <div className='px-6 py-3'>

        {error && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            <span className='min-w-0 flex-1'>{error}</span>
            {/* Offered for any load failure, not just rate limits: whatever went
                wrong, asking again is the one thing the user can usefully do,
                and it costs less than reloading the whole browser tab. */}
            <Button
              variant='outline'
              size='sm'
              className='shrink-0'
              onClick={() => {
                setError(null)
                setReloadKey(k => k + 1)
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {loading ? (
          <div className='flex items-center justify-center py-24'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : (
          procedure &&
          journey && (
            <div className='flex min-h-[36rem] rounded-xl border bg-card'>
              {/* Left rail */}
              <aside className='w-64 shrink-0 border-r bg-muted/10'>
                <ProcedureFormSidebar
                  procedureName={journey.procedureTemplate.name}
                  steps={stepsWithAdHoc}
                  selectedStepId={selectedStepId}
                  selectedRepeatIndex={selectedRepeatIndex}
                  onSelect={(s, repeat) => {
                    setSelectedStepId(s.stepId)
                    // Clearing on a non-repeat select matters: moving from
                    // "Step 2 of 3" to a plain step must not leave the page
                    // resolving a repeat that the new step does not have.
                    setSelectedRepeatIndex(repeat?.repeatIndex ?? null)
                  }}
                  categoryByStepId={stepCategories}
                  anchoredFieldStepIds={anchoredFieldStepIds}
                  adHocStepIds={adHocStepIds}
                  onViewFlow={() => setFlowPreviewOpen(true)}
                  canAssign={canAssign}
                  assignableUsers={assignableUsers}
                  onAssignStep={saveStepAssign}
                  assignSavingStepId={assignSavingStepId}
                  onAssignRepeat={saveRepeatAssign}
                  assignSavingRepeatKey={assignSavingRepeatKey}
                />
              </aside>

              {/* Main content */}
              <main className='min-w-0 flex-1 space-y-3 p-3'>
                {!isAdHocStep && <AnchoredFieldsPanel items={anchoredItems} />}
                <ProcedureAttachmentsPanel entries={procedureAttachmentEntries} />

                <div className='rounded-xl border'>
                  <FormActionBar
                    formName={selectedStep?.formName}
                    completed={selectedSubmission?.status === 'completed'}
                    pendingReview={selectedSubmission?.status === 'pending_review'}
                    // Kits too (z8nrz7e2mu): unlocking a kit form takes its kit
                    // back out of Kit Inventory. The server refuses once any
                    // unit of the kit has been used.
                    onUnlock={canUnlock && selectedSubmission ? () => setUnlockOpen(true) : undefined}
                    skipped={selectedSubmission?.status === 'skipped'}
                    onSkip={skippable ? () => setSkipOpen(true) : undefined}
                    onUnskip={unskippable ? () => void handleUnskip() : undefined}
                    allowedActions={
                      offersActionItems
                        ? selectedDefinition?.allowedActions
                        : selectedDefinition?.allowedActions?.filter(a => a !== ACTION_ITEMS_ACTION)
                    }
                    actionItemCount={actionItemCount.data?.count}
                    onOpenActionItems={
                      offersActionItems && selectedSubmission
                        ? () =>
                            navigate(
                              formItemsLink({
                                submissionId: selectedSubmission.id,
                                formName: selectedStep?.formName ?? 'this form',
                                path: formSourcePath(pathname, selectedSubmission.id),
                              }),
                            )
                        : undefined
                    }
                    canShipProducts={canShip}
                    onUploadForm={handleUploadForm}
                    onProductOperation={value => {
                      if (value === 'Product: Receive product') setReceiveProductOpen(true)
                      else if (value === 'Product: Split product') setSplitProductOpen(true)
                      else if (value === 'Product: Pool / combine product') setPoolProductOpen(true)
                      else if (value === 'Product: Update product') setPickerIntent('volume')
                      // Store IS place/move — the same designated-location
                      // prompt, reached for a product that already exists.
                      else if (value === 'Product: Store product') setPickerIntent('store')
                      // Opens the product list on the procedure's products; its truck
                      // icon opens the ship popup.
                      else if (value === 'Product: Ship product') setManageProductsOpen(true)
                      else setCreateProductOpen(true)
                    }}
                    onDisplayPatientHistory={() => {
                      setPatientHistoryOpen(true)
                      // The rows already held show at once; their progress refreshes.
                      void otherProcedures.refetch()
                    }}
                    patientHistoryCount={otherProcedures.data?.length ?? 0}
                    patientName={isKit || isMes || isTask ? null : patientName}
                    productCount={mainProducts.length}
                    productLabel={productLabel(working.selected)}
                    onManageProducts={() => setManageProductsOpen(true)}
                    workingProducts={
                      selectedSubmission && formEditable
                        ? {
                            choices: working.offered.map(p => ({
                              id: p.id,
                              label: `${p.din} / ${p.productDescriptionCode}`,
                            })),
                            selectedIds: working.selected.map(p => p.id),
                            needsChoice: working.needsChoice,
                            formKey: selectedSubmission.id,
                            onSave: handleSaveWorkingProducts,
                          }
                        : undefined
                    }
                    rightSlot={
                      <div className='flex items-center gap-2'>
                        <LisReviewButton
                          scope={{ procedureId }}
                          onDecided={handleLisDecided}
                          currentSubmissionId={selectedSubmission?.id ?? null}
                        />
                        {canAssign && selectedStep && (
                          <AssigneeSelect
                            variant='avatar'
                            users={assignableUsers}
                            value={selectedStep.assignee ?? null}
                            disabled={assignSavingStepId === selectedStep.stepId}
                                  meId={user?.userId}
                            onChange={assigneeId => saveStepAssign(selectedStep, assigneeId)}
                          />
                        )}
                        {selectedSubmission && selectedDefinition && !isAdHocStep && (
                          <PopulateFromInstrument
                            submission={selectedSubmission}
                            onSubmissionUpdated={handleSubmissionUpdated}
                          />
                        )}
                      </div>
                    }
                    onAction={handleFormAction}
                  />
                  <div className='space-y-3 p-3'>
                    {/* The label reconciliation the form completed with (z8nrz7dt48 AC7). */}
                    {appliedResults && appliedResults.submissionId === selectedSubmission?.id && (
                      <div
                        role='status'
                        className='flex items-start justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                      >
                        <span>
                          {appliedResults.applied} test result{appliedResults.applied === 1 ? '' : 's'}
                          {appliedResults.stage ? ` for ${appliedResults.stage}` : ''} applied to{' '}
                          <span className='font-mono'>
                            {appliedResults.din} / {appliedResults.productCode}
                          </span>
                          .
                          {!appliedResults.lims.applied && appliedResults.lims.reason
                            ? ` The product's figures were not derived: ${appliedResults.lims.reason}`
                            : ''}
                        </span>
                        <button
                          type='button'
                          className='text-xs underline'
                          onClick={() => setAppliedResults(null)}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    {selectedSubmission?.status !== 'active' && labelSummary.data?.recorded && (
                      <RecordedReconciliationBanner
                        recorded={labelSummary.data.recorded}
                        onOpenLabels={() => setLabelsOpen(true)}
                      />
                    )}
                    <SelectedForm
                      step={selectedStep}
                      definition={selectedDefinition}
                      definitionMissing={
                        !!selectedStep && missingDefinitionIds.has(selectedStep.formDefinitionId)
                      }
                      submission={selectedSubmission}
                      starting={startingStepId === selectedStep?.stepId}
                      // Starts the repeat the rail has open, not just "the step".
                      onStart={step => void handleStart(step, selectedRepeatIndex)}
                      onSubmissionUpdated={handleSubmissionUpdated}
                      crossForm={crossForm}
                      beforeComplete={handleBeforeComplete}
                      completeTrigger={completeTrigger}
                      completionExtras={completionExtras}
                    />
                  </div>
                </div>
              </main>
            </div>
          )
        )}
      </div>

      {selectedSubmission && (
        <ActionItemDialog
          open={actionItemOpen}
          onOpenChange={setActionItemOpen}
          formSource={{
            submissionId: selectedSubmission.id,
            formName: selectedStep?.formName ?? 'this form',
            path: formSourcePath(pathname, selectedSubmission.id),
          }}
        />
      )}

      <LisOrderDialog
        open={orderOpen}
        onOpenChange={setOrderOpen}
        stepName={selectedStep?.formName}
        mrn={mrn}
        procedureId={procedureId}
        formDefinitionId={selectedStep?.formDefinitionId}
      />

      {selectedSubmission && selectedDefinition && (
        <ApplyTestResultsDialog
          open={applyResultsOpen}
          onOpenChange={setApplyResultsOpen}
          definition={selectedDefinition}
          submission={selectedSubmission}
          products={procedureProducts}
          signerName={user?.fullName || user?.email}
          onValuesSaved={handleSubmissionUpdated}
          onApplied={result => {
            setAppliedResults({ ...result, submissionId: selectedSubmission.id })
            invalidateAuditTrail()
            void queryClient.invalidateQueries({ queryKey: ['products'] })
          }}
        />
      )}

      <LisSendToEpicDialog
        open={epicOpen}
        onOpenChange={setEpicOpen}
        procedureId={procedureId}
        initialProductId={epicProductId}
      />

      {procedure && journey && (
        <ProcedureFlowPreviewDialog
          open={flowPreviewOpen}
          onOpenChange={setFlowPreviewOpen}
          procedureTemplateId={procedure.procedureTemplateId}
          procedureName={journey.procedureTemplate.name}
          definitions={Array.from(defsById.values())}
        />
      )}

      {selectedStep && (
        <CreateProductDialog
          open={createProductOpen}
          onClose={() => setCreateProductOpen(false)}
          submissionId={selectedSubmission?.id ?? ''}
          formDefinitionId={selectedStep.formDefinitionId}
          procedureId={procedureId}
          patientMrn={mrn}
          patientName={patientName}
          patientDob={patientQuery.data?.birthDate ?? null}
          requiresPatientHistory={selectedDefinition?.requiresPatientHistory ?? true}
          onCreated={product => {
            setLabelPrintProduct(product)
          }}
        />
      )}

      {selectedStep && (
        <ReceiveProductDialog
          open={receiveProductOpen}
          onClose={() => setReceiveProductOpen(false)}
          submissionId={selectedSubmission?.id ?? ''}
          formDefinitionId={selectedStep.formDefinitionId}
          procedureId={procedureId}
          patientMrn={mrn}
          patientName={patientName}
          patientDob={patientQuery.data?.birthDate ?? null}
          requiresPatientHistory={selectedDefinition?.requiresPatientHistory ?? true}
          onReceived={product => {
            // Received bags go straight to placement — the label is already on
            // the bag when it comes from outside.
            setStorageProduct(product)
          }}
        />
      )}

      {selectedStep && (
        <SplitProductDialog
          open={splitProductOpen}
          onClose={() => setSplitProductOpen(false)}
          submissionId={selectedSubmission?.id ?? ''}
          formDefinitionId={selectedStep.formDefinitionId}
          procedureId={procedureId}
          patientMrn={mrn}
          // A lab value the forms do not hold can be ordered from here; the
          // split waits for nobody, so its dialog closes for the order dialog.
          onOrderTest={() => {
            setSplitProductOpen(false)
            setOrderOpen(true)
          }}
          onSplit={(children, parent, parentFullyConsumed) => {
            // A split uses up the whole container (z8nrz7g2wm): the server
            // leaves the parent at 0 mL, and its slot is freed here, through
            // the storage route so the slot's own audit records it.
            if (parentFullyConsumed && parent.storageNodeId) {
              setProductLocation.mutate({ productId: parent.id, storageNodeId: null })
            }
            setSplitLabelProducts(children)
          }}
        />
      )}

      {selectedStep && (
        <PoolProductDialog
          open={poolProductOpen}
          onClose={() => setPoolProductOpen(false)}
          submissionId={selectedSubmission?.id ?? ''}
          formDefinitionId={selectedStep.formDefinitionId}
          procedureId={procedureId}
          patientMrn={mrn}
          onPooled={(pooled, sources) => {
            // The sources were physically emptied into the pooled bag, so the
            // slots they occupied are now free. Same reasoning as a split with
            // no remainder — there is no "consumed" flag on a product, and
            // leaving them marked as occupying a freezer position would be a
            // lie the storage views would repeat.
            for (const source of sources) {
              if (source.storageNodeId) {
                setProductLocation.mutate({ productId: source.id, storageNodeId: null })
              }
            }
            // A pooled bag is a NEW product, so it runs the ordinary Create
            // sequence from here: Label Printing → the printed label → the
            // designated-location prompt.
            setLabelFlow('create')
            setLabelPrintProduct(pooled)
          }}
        />
      )}

      <SelectProductDialog
        open={pickerIntent !== null}
        title={pickerIntent === 'store' ? 'Store product' : 'Update product'}
        description={
          pickerIntent === 'store'
            ? 'Choose the product to place or move in storage.'
            : 'Choose the product whose recorded volume needs correcting.'
        }
        patientMrn={mrn}
        onClose={() => setPickerIntent(null)}
        onSelect={product => {
          const intent = pickerIntent
          setPickerIntent(null)
          if (intent === 'store') {
            setStorageProduct(product)
          } else {
            setVolumeProduct(product)
          }
        }}
      />

      <ProductAuditTrailDialog product={auditProduct} onClose={() => setAuditProduct(null)} />

      <LimsDetailsDialog product={limsProduct} onClose={() => setLimsProduct(null)} />

      <UpdateProductVolumeDialog
        product={volumeProduct}
        onClose={() => setVolumeProduct(null)}
      />

      <SplitLabelPreviewDialog
        products={splitLabelProducts}
        onClose={() => {
          const batch = splitLabelProducts
          setSplitLabelProducts([])
          setBatchStorageProducts(batch)
        }}
      />

      <BatchStorageAssignmentDialog
        products={batchStorageProducts}
        onClose={() => setBatchStorageProducts([])}
      />

      <LabelPrintingDialog
        product={labelPrintProduct}
        onClose={() => setLabelPrintProduct(null)}
        onPrinted={(product, selections) => {
          // Per the spec sequence: options popup → the printed label itself
          // (preview + physical print) → the designated-location prompt.
          // The selections ride along so the preview shows the messages that
          // were just chosen, not a label missing them.
          setLabelPrintProduct(null)
          setLabelSelections(selections)
          setLabelPreviewProduct(product)
        }}
      />

      <ProductLabelPreviewDialog
        product={labelPreviewProduct}
        selections={labelSelections}
        printKind={labelFlow === 'create' ? 'original' : labelFlow}
        onClose={() => {
          const product = labelPreviewProduct
          setLabelPreviewProduct(null)
          setLabelSelections(undefined)
          // Only the create sequence continues into placement; a relabel is a
          // reprint of a bag that is already wherever it already is.
          if (labelFlow === 'create') setStorageProduct(product)
          setLabelFlow('create')
        }}
      />

      {selectedSubmission && (
        <UnlockFormDialog
          submissionId={selectedSubmission.id}
          open={unlockOpen}
          onOpenChange={setUnlockOpen}
          signerName={user?.fullName || user?.email}
          onUnlocked={handleUnlocked}
          isKit={isKit}
        />
      )}

      {/* "+" → Skip Form: the reason goes on the audit trail (z8nrz7fn5r). */}
      <SkipFormDialog
        open={skipOpen}
        onOpenChange={setSkipOpen}
        formName={selectedStep?.formName}
        onSkip={handleSkip}
      />

      <ShipProductDialog
        open={shipProducts !== null}
        // Back to the product list the truck icon was clicked in.
        onClose={() => {
          setShipProducts(null)
          setManageProductsOpen(true)
        }}
        initialProducts={shipProducts ?? []}
      />

      <ManageProductsDialog
        open={manageProductsOpen}
        onClose={() => setManageProductsOpen(false)}
        products={formProducts}
        isLoading={formProductsQuery.isLoading}
        procedureProducts={procedureProducts}
        procedureProductsLoading={procedureProductsQuery.isLoading}
        patientProducts={patientProductsQuery.data ?? []}
        patientProductsLoading={patientProductsQuery.isLoading}
        patientMrn={mrn || null}
        onReprint={product => {
          // Reprint reproduces the label that was printed, messages included —
          // they are stored on the product for exactly this, since holding
          // them in memory lost them on the first reload. It goes through the
          // preview like any print: signed before it prints, and recorded as a
          // new label replacing the last (z8nrz7dt48).
          setManageProductsOpen(false)
          setLabelFlow('reprint')
          setLabelSelections(storedSelectionsOf(product))
          setLabelPreviewProduct(product)
        }}
        onOpenLabels={
          selectedSubmission
            ? () => {
                setManageProductsOpen(false)
                setLabelsOpen(true)
              }
            : undefined
        }
        pendingLabelCount={labelSummary.data?.pending ?? 0}
        onChangeLabel={product => {
          setManageProductsOpen(false)
          setLabelFlow('relabel')
          setLabelPrintProduct(product)
        }}
        onViewHistory={product => {
          setManageProductsOpen(false)
          setAuditProduct(product)
        }}
        onShip={canShip ? product => {
          setManageProductsOpen(false)
          setShipProducts([product])
        } : undefined}
        onViewLims={product => {
          setManageProductsOpen(false)
          setLimsProduct(product)
        }}
        onSendToEpic={product => {
          setManageProductsOpen(false)
          setEpicProductId(product.id)
          setEpicOpen(true)
        }}
        onUpdateVolume={product => {
          // The volume dialog carries the product's own trail, so it replaces
          // the panel rather than stacking on top of it.
          setManageProductsOpen(false)
          setVolumeProduct(product)
        }}
        onRelocate={product => {
          setManageProductsOpen(false)
          // The dialog reads the product's own catalog entry for its storage
          // requirement.
          setStorageProduct(product)
        }}
      />

      <StorageAssignmentDialog
        product={storageProduct}
        onClose={() => setStorageProduct(null)}
      />

      <RunLabelsDialog
        submissionId={selectedSubmission?.id ?? null}
        open={labelsOpen}
        onClose={() => setLabelsOpen(false)}
      />
      <LabelReconciliationDialog
        summary={labelSummary.data}
        open={labelReconcileOpen}
        onClose={() => setLabelReconcileOpen(false)}
        onReview={() => {
          setLabelReconcileOpen(false)
          setLabelsOpen(true)
        }}
        onContinue={() => {
          setLabelReconcileOpen(false)
          if (!selectedSubmission || !labelSummary.data) return
          setLabelReviewed({ submissionId: selectedSubmission.id, key: labelCountsKey(labelSummary.data) })
          setCompleteTrigger(t => t + 1)
        }}
        onCompleteWithReason={reason => {
          setLabelReconcileOpen(false)
          if (!selectedSubmission || !labelSummary.data) return
          setLabelDiscrepancy({ submissionId: selectedSubmission.id, reason })
          setLabelReviewed({ submissionId: selectedSubmission.id, key: labelCountsKey(labelSummary.data) })
          setCompleteTrigger(t => t + 1)
        }}
      />

      <ProductNotCreatedDialog
        open={productGatePromptOpen}
        onClose={() => setProductGatePromptOpen(false)}
        onCreateProduct={() => {
          setProductGatePromptOpen(false)
          setCreateProductOpen(true)
        }}
        onSkip={handleSkipProductGate}
      />

      <AddFormDialog
        open={addFormOpen}
        onClose={() => setAddFormOpen(false)}
        defaultCategory={addFormCategory}
        onSelect={handleAddFormSelect}
        loading={addFormLoading}
      />

      <PatientHistoryPanel
        open={patientHistoryOpen}
        onOpenChange={setPatientHistoryOpen}
        procedures={otherProcedures.data ?? []}
        loading={otherProcedures.isPending}
        error={otherProcedures.error}
        onRetry={() => void otherProcedures.refetch()}
        patientMrn={mrn}
      />
    </div>
  )
}

// ─── Inline form area for the selected step ─────────────────────────────────────

function SelectedForm({
  step,
  definition,
  definitionMissing,
  submission,
  starting,
  onStart,
  onSubmissionUpdated,
  crossForm,
  beforeComplete,
  completeTrigger,
  completionExtras,
}: {
  step: ProcedureJourneyStep | null
  definition: FormDefinition | null
  /** The form could not be loaded at all: say so rather than spin. */
  definitionMissing?: boolean
  submission: FormSubmission | null
  starting: boolean
  onStart: (step: ProcedureJourneyStep) => void
  onSubmissionUpdated: (updated: FormSubmission) => void
  crossForm?: CrossFormResolution
  beforeComplete?: () => boolean
  completeTrigger?: number
  completionExtras?: CompletionExtras
}) {
  const { selectedTenant } = useTenant()
  // The signed-in person — what the signature dialogs show, and who Sign as
  // compares against. It used to be a hardcoded "Demo User".
  const currentUser = useCurrentCgtUser()
  if (!step) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        Select a form from the left to view it here.
      </p>
    )
  }

  const waitingOnLag = !!step.availableAt && new Date(step.availableAt) > new Date()

  // Repeats are not sequenced (z8nrz7e1be): a repeat is only ever locked
  // because its step is, so that case falls straight through to the step's
  // own lock message below — there is no separate "this performance is
  // waiting on an earlier one" state any more.
  if (step.status === 'locked') {
    return (
      <div className='py-8 text-center'>
        <Lock className='mx-auto mb-3 h-6 w-6 text-slate-300' />
        <p className='text-sm font-medium'>{step.formName} is locked</p>
        {step.blockedBy.length > 0 && (
          <p className='mt-1 text-xs text-muted-foreground'>
            Waiting on {step.blockedBy.map(b => b.formName).join(', ')}
          </p>
        )}
        {step.blockedBy.length === 0 && waitingOnLag && (
          <p className='mt-1 text-xs text-amber-600'>
            Minimum lag not met — can be filled on{' '}
            {new Date(step.availableAt!).toLocaleDateString()}
          </p>
        )}
      </div>
    )
  }

  // Not started yet — offer to start it inline.
  if (!submission) {
    return (
      <div className='py-8 text-center'>
        <h3 className='text-sm font-semibold'>{step.formName}</h3>
        <p className='mt-1 text-xs text-muted-foreground'>This form hasn’t been started yet.</p>
        {step.dueAt && (
          <p
            className={
              'mt-1 text-xs ' +
              (new Date(step.dueAt) < new Date() ? 'text-rose-600 font-medium' : 'text-amber-600')
            }
          >
            {new Date(step.dueAt) < new Date() ? 'Overdue — was due ' : 'Complete by '}
            {new Date(step.dueAt).toLocaleDateString()}
          </p>
        )}
        <Button className='mt-4' onClick={() => onStart(step)} disabled={starting}>
          {starting ? (
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          ) : (
            <PlayCircle className='mr-2 h-4 w-4' />
          )}
          Start {step.formName}
        </Button>
      </div>
    )
  }

  if (!definition && definitionMissing) {
    return (
      <div className='py-8 text-center'>
        <AlertCircle className='mx-auto mb-3 h-6 w-6 text-destructive' />
        <p className='text-sm font-medium'>{step.formName} could not be loaded</p>
        <p className='mt-1 text-xs text-muted-foreground'>
          Its form definition was not found. Reload the page, or ask an administrator to check the form.
        </p>
      </div>
    )
  }

  if (!definition) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    )
  }

  // "Populate from instrument" lives in the FormActionBar above; the renderer's
  // own name/MRN/status header is hidden because the action bar and patient
  // banner already show that context.
  return (
    <>
      <FormRenderer
        key={submission.id}
        definition={definition}
        submission={submission}
        currentUser={currentUser}
        onSubmissionUpdated={onSubmissionUpdated}
        hideHeader
        crossForm={crossForm}
        tenantTimezone={selectedTenant?.timezone}
        beforeComplete={beforeComplete}
        completeTrigger={completeTrigger}
        completionExtras={completionExtras}
      />
    </>
  )
}
